import { LiveLegProgress } from './LiveLegProgress'
import { useOddsStore, type LegOddsData } from '../../stores/oddsStore'
import { formatOdds, type OddsFormat } from '../../utils/odds'
import {
  getSelections,
  selectionPresentation,
  selectionSetIsMultipleSingles,
} from '../../utils/selectionSet'
import { useOddsDirectionFlash } from '../../hooks/useOddsDirectionFlash'
import type { Selection, SelectionGroup, SelectionSet } from '../../types/feedContract'
import { useFeedConfig } from '../../contexts/feedConfigContext'
import { useI18n, type Translator } from '../../i18n'
import { useOddsFormat } from '../../i18n/hostFormat'

interface CardSelectionsProps {
  selectionSet: SelectionSet
  postId: string
  /** Overrides the host's odds format (hostFormat). */
  oddsFormat?: OddsFormat
}

function getSelectionStatus(selection: Selection, t: Translator['t'], liveData?: LegOddsData): { label: string; className: string } {
	if (liveData?.isSuspended) return { label: t('leg.suspended'), className: 'leg-suspended' }
	if (liveData?.isClosed) return { label: t('leg.closed'), className: 'leg-closed' }

	const result = selection.status
	if (result === 'won' || result === 'win') return { label: t('leg.won'), className: 'leg-won' }
	if (result === 'lost' || result === 'loss') return { label: t('leg.lost'), className: 'leg-lost' }
	return { label: '', className: 'leg-open' }
}

// Leg odds text stays a neutral color at all times (only the combined
// top-line price flashes color); only the direction arrow gets the
// restrained flash-and-fade treatment, keyed to genuine price changes.
function LegOdds({ value, isLivePrice, format }: { value: string; isLivePrice: boolean; format: OddsFormat }) {
  const { flashKey, direction, clearFlash } = useOddsDirectionFlash(value, isLivePrice, format)

  return (
    <span className="leg-odds">
      {value}
      {direction === 'increased' && (
        <span
          key={flashKey}
          className="odds-arrow odds-arrow-up"
          aria-hidden="true"
          onAnimationEnd={clearFlash}
        >
          &#9650;
        </span>
      )}
      {direction === 'decreased' && (
        <span
          key={flashKey}
          className="odds-arrow odds-arrow-down"
          aria-hidden="true"
          onAnimationEnd={clearFlash}
        >
          &#9660;
        </span>
      )}
    </span>
  )
}

type Segment =
  | { type: 'group'; group: SelectionGroup; selections: Selection[] }
  | { type: 'single'; selection: Selection }

export function CardSelections({ selectionSet, postId, oddsFormat: oddsFormatOverride }: CardSelectionsProps) {
  const { branding } = useFeedConfig()
  const { t } = useI18n()
  const hostOddsFormat = useOddsFormat()
  const oddsFormat = oddsFormatOverride ?? hostOddsFormat
  const presentationConfig = branding.presentation.betslip
  const markerIconUrl = branding.assets?.betslipMarkerIconUrl || ''
  const oddsData = useOddsStore((s) => s.getOddsForActivity(postId))
  const selections = getSelections(selectionSet)
  const isMultipleSingles = selectionSetIsMultipleSingles(selectionSet)
  const showConnectors = !isMultipleSingles || presentationConfig.connectMultipleSingles
  const selectionById = new Map(selections.map((selection): [string, Selection] => [selection.id, selection]))
  const displayGroups = Array.isArray(selectionSet?.groups)
    ? selectionSet.groups.filter((group) => Array.isArray(group.selection_ids) && group.selection_ids.length > 1)
    : []
  const groupedSelectionIds = new Set(displayGroups.flatMap((group) => group.selection_ids))

  const segments: Segment[] = []
  for (const selection of selections) {
    const group = displayGroups.find((candidate) => candidate.selection_ids?.[0] === selection.id)
    if (group) {
      segments.push({
        type: 'group',
        group,
        selections: group.selection_ids.map((id) => selectionById.get(id)).filter(Boolean),
      })
      continue
    }
    if (!groupedSelectionIds.has(selection.id)) {
      segments.push({ type: 'single', selection })
    }
  }

  let globalIdx = 0

  function renderSelection(selection: Selection, connectFromPrevious: boolean, connectToNext: boolean) {
    const idx = globalIdx++
    const presentation = selectionPresentation(selection)
    const liveData = oddsData?.legs?.[presentation.outcomeId]
    const status = getSelectionStatus(selection, t, liveData)
    const displayOdds = formatOdds(
      liveData?.currentOdds ? { american: liveData.currentOdds, decimal: liveData.currentDecimal } : presentation.odds,
      oddsFormat,
    )

    return (
      <div key={idx} className={`card-leg ${status.className}`}>
        {showConnectors && (
          <div className={`leg-connector${markerIconUrl ? ' leg-connector--custom-marker' : ''}`}>
            {markerIconUrl ? (
              <img className="leg-connector-marker" src={markerIconUrl} alt="" aria-hidden="true" />
            ) : null}
            {connectFromPrevious && (
              <div className="leg-connector-line leg-connector-line--incoming" />
            )}
            {connectToNext && (
              <div className="leg-connector-line leg-connector-line--outgoing" />
            )}
          </div>
        )}
        <div className="leg-details">
          <div className="leg-selection-name">{presentation.selection}</div>
          <div className="leg-market-name">{presentation.market}</div>
          <LiveLegProgress selection={selection} odds={liveData} />
          {presentationConfig.showLegEvent ? (
            <div className="leg-event-name">
              {presentation.event}
              {liveData?.isLive && <span className="leg-live-badge">{t('leg.live')}</span>}
            </div>
          ) : null}
        </div>
        <div className="leg-odds-container">
          <LegOdds value={displayOdds} isLivePrice={Boolean(liveData?.currentOdds)} format={oddsFormat} />
          {status.label && (
            <div className={`leg-status-badge ${status.className}`}>
              {status.label}
            </div>
          )}
        </div>
      </div>
    )
  }

  return (
    <div className="card-legs">
      {segments.map((segment, segIdx) => {
        if (segment.type === 'group') {
          return (
            <div key={`sgp-${segIdx}`} className="leg-sgp-group">
              {presentationConfig.showGroupLabel ? (
                <div className="leg-sgp-label">{segment.group.label || t('leg.grouped')}</div>
              ) : null}
              {segment.selections.map((selection, selectionIdx) =>
                renderSelection(
                  selection,
                  selectionIdx > 0,
                  selectionIdx < segment.selections.length - 1,
                )
              )}
            </div>
          )
        }
        const previousSegment = segments[segIdx - 1]
        const nextSegment = segments[segIdx + 1]
        return renderSelection(
          segment.selection,
          previousSegment?.type === 'single',
          nextSegment?.type === 'single',
        )
      })}
    </div>
  )
}

export const CardLegs = CardSelections
